import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useAtomValue, useSetAtom } from 'jotai';

import type {
  ProjectCompaniesSort,
  ProjectListing,
} from '@/api/v4/projects.api';
import { RelatedProjectsCount } from '@/features/companies/related-projects/related-projects-count';
import { SuspenseFallback } from '@/components/suspense-fallback/suspense-fallback';
import { getListingStageName } from '@/features/listing-stage/helpers/get-listing-stage-name';
import { useListingStages } from '@/features/listing-stage/use-listing-stages.query';
import { CompanyNameTableCell } from './company-name-cell';
import { formatDate } from '@/helpers/format-date';
import { formatNotApplicable } from '@/helpers/format-empty';
import { getAbbreviateNumber } from '@/helpers/format-number';
import { listingSidebarIdState } from '@/features/projects/project.state';
import { TagsPreview } from '@/features/tags/tags-preview';
import { useResponsiveTagsCount } from '@/features/tags/use-responsive-tags-count';
import { pendingProjectListingStageIdsAtom } from '@/state/project-listing-pending-stage.state';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/setupTheme';
import { Button } from '@/ui/button/button';
import { WithDropdown } from '@/ui/dropdown/dropdown';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import type { Row } from '@/ui/table/table.types';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { ListingMenu } from '../stage-management-table/table/listing-menu';

import { generateListingStageMenu } from './generate-listing-stage-menu';
import { ProjectListingTableHeader } from './project-listing-table-header-config';

const STAGE_BUTTON_WIDTH = 160;

type UseProjectCompanyTableConfigurationProps = {
  data: ProjectListing[];
  onListingStatusChanged: (
    listingId: string,
    newListStatusId: string | null,
    isDeal?: boolean,
  ) => void;
};

export const useProjectListingsTableConfiguration = ({
  data,
  onListingStatusChanged,
}: UseProjectCompanyTableConfigurationProps) => {
  const headers = useTranslateHeaders<ProjectCompaniesSort>(
    ProjectListingTableHeader,
    'companies',
    'companies',
  );
  const { t } = useTranslation('companies');

  const { hasRequiredPermission } = useUserPermissions();
  const { listingStages } = useListingStages();
  const maxVisibleTags = useResponsiveTagsCount();
  const setListingSidebarState = useSetAtom(listingSidebarIdState);

  const canChangeCompanyStage = hasRequiredPermission(
    PERMISSION.UPDATE_COMPANY_LISTING_STATUS,
  );

  const pendingProjectListingStageIds = useAtomValue(
    pendingProjectListingStageIdsAtom,
  );

  const rows = useMemo<Row[]>(() => {
    if (!listingStages?.length) {
      return [];
    }

    return data.map(listing => {
      const rowBackgroundColor = listing.notRelevant
        ? theme.colors.gray.c1
        : undefined;

      const listingStatusMenu = listingStages?.length
        ? generateListingStageMenu({
            isNotRelevant: listing.notRelevant,
            listingId: listing.id,
            listingStages,
            onListingStatusChanged,
            status: listing.status,
          })
        : [];

      return {
        id: listing.organization.id,
        rowPadding: {
          left: '10px',
          right: '0px',
        },
        rowBackgroundColor,
        rowHover: true,
        onClick: () => setListingSidebarState(listing.id),
        cells: [
          {
            value: (
              <CompanyNameTableCell
                listing={listing}
                shouldLinkToCompany={hasRequiredPermission(
                  PERMISSION.VIEW_ORGANIZATION_HOME_SCREEN,
                )}
              />
            ),
          },
          {
            value: (
              <TagsPreview
                tags={listing.organization.tags}
                maxVisibleTags={maxVisibleTags}
              />
            ),
            minWidth: '160px',
          },
          {
            value: (
              <RelatedProjectsCount
                value={listing.organization.projectListingsCount ?? 0}
                companyId={listing.organization.id}
              />
            ),
          },
          {
            value: (
              <RowContainer
                isGreyOut={listing.notRelevant}
                name="company-year-founded"
                justify="start"
              >
                {listing.organization.yearEstablished}
              </RowContainer>
            ),
          },
          {
            value: (
              <RowContainer
                isGreyOut={listing.notRelevant}
                name="company-funding-stage"
                justify="start"
              >
                {formatNotApplicable(listing.organization.fundingStage?.name)}
              </RowContainer>
            ),
          },
          {
            value: (
              <RowContainer
                isGreyOut={listing.notRelevant}
                name="company-capital-raised"
                justify="start"
              >
                {listing.organization.capitalRaised
                  ? getAbbreviateNumber(
                      listing.organization.capitalRaised.toString(),
                    )
                  : formatNotApplicable(listing.organization.capitalRaised)}
              </RowContainer>
            ),
          },
          {
            value: (
              <RowContainer isGreyOut={listing.notRelevant}>
                {listing.lastEditedDate
                  ? formatDate(listing.lastEditedDate, {
                      format: 'dd MMM, yyyy',
                    })
                  : ''}
              </RowContainer>
            ),
          },

          {
            value: (
              <RowContainer
                name="company-listing-stage"
                dataTestId="company-listing-stage"
                justify="left"
                padding="0px 12px"
              >
                <WithDropdown
                  disabled={
                    !canChangeCompanyStage ||
                    pendingProjectListingStageIds.has(listing.id)
                  }
                  testId={`listing-row-company-stage-menu-${listing.organization.id}`}
                  items={listingStatusMenu}
                  options={{ placement: 'bottom-end' }}
                  maxWidth="auto"
                >
                  <CompanyStageButton
                    variant={listing.notRelevant ? 'gray' : 'purple'}
                    endIcon={canChangeCompanyStage ? 'DownSm' : undefined}
                    width={STAGE_BUTTON_WIDTH}
                  >
                    {listing.notRelevant ? (
                      t`listingStatuses.notRelevant`
                    ) : (
                      <EllipsisText maxWidth={`${STAGE_BUTTON_WIDTH}px`}>
                        {getListingStageName(listing.status, listingStages)}
                      </EllipsisText>
                    )}
                  </CompanyStageButton>
                </WithDropdown>
              </RowContainer>
            ),
          },
          {
            value: (
              <SuspenseFallback>
                <ListingMenu
                  key={listing.id}
                  listing={listing}
                  blacklist={['moveToStage']}
                />
              </SuspenseFallback>
            ),
            hoverOnly: true,
            align: 'right',
            verticalAlign: 'middle',
            padding: '0',
          },
        ],
      };
    });
  }, [
    canChangeCompanyStage,
    data,
    hasRequiredPermission,
    listingStages,
    maxVisibleTags,
    onListingStatusChanged,
    pendingProjectListingStageIds,
    setListingSidebarState,
    t,
  ]);

  return { rows, headers };
};

const RowContainer = styled(Flexbox)<{
  isGreyOut?: boolean;
}>`
  ${({ isGreyOut, theme }) =>
    `color: ${isGreyOut ? theme.colors.gray.c7 : colors.gray.c12}`};
`;

const EllipsisText = styled('p', forwardRef)<{ maxWidth: string }>`
  ${({ theme }) => theme.mixins.ellipsis}
  max-width: ${({ maxWidth }) => maxWidth};
`;

const CompanyStageButton = styled(Button)`
  border-radius: 4px;
  padding: 7px 8px;
`;
